import { LoadingJobDetails } from '@warebee/frontend/data-access-api-graphql';
import { format } from 'date-fns';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getQualitativeColor } from '../../common/color.helper';
import { toDateFromLocaleStringDate } from '../../common/dateTimeHelper';
import useFormatter from '../../common/useFormatter';
import ButtonLocateLocation from '../../components/ButtonLocateLocation';
import { ColumnConfig } from '../../components/DatasetTable';
import Tag from '../../helpContext/Tag';
import { renderUomsWithTotal } from '../panels/allocate/useAllocationSummaryTableConfig';
import {
  analyzeGateUsageScheduleCustomThresholds,
  analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  analyzeGateUsageScheduleWaitTimeLateThreshold,
  analyzeGateUsageScheduleWaitTimeWarningThreshold,
} from '../store/analyze.state';
import EmptyValueTag from '../tags/EmptyValueTag';
import ItemTag from '../tags/ItemTag';
import { JobTimelineDataTableRow } from './SimulationJobTimelineDataTable';
import { WaitTimeClassificationModel } from './waitTimeClassificationModel';

function useSimulationJobTimelineTableConfig(): ColumnConfig<JobTimelineDataTableRow>[] {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();
  const warningThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeWarningThreshold,
  );
  const lateThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeLateThreshold,
  );
  const criticalThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  );
  const customThresholds = useRecoilValue(
    analyzeGateUsageScheduleCustomThresholds,
  );

  const config: ColumnConfig<JobTimelineDataTableRow>[] = [
    {
      field: 'resourceId',
      title: t`Dock`,
      hasSort: true,
      hasFilter: true,
      render: (v: any) => {
        const color = getQualitativeColor(v, 'policy');
        return (
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded-sm"
              style={{ backgroundColor: color[0] }}
            />
            <ButtonLocateLocation locationId={v} />
          </div>
        );
      },
    },

    {
      field: 'job.id',
      title: t`Job ID`,
      hasSort: true,
      hasFilter: true,
      render: (v: any, row) => (
        <ItemTag title={v} filters={{ shipmentId: row[row.job.id] }} />
      ),
    },
    {
      field: 'job.deadline',
      title: t`Wait Time`,
      isHeader: true,
      render: (v: number, row) => {
        const deadline = toDateFromLocaleStringDate(row.job.deadline);
        const startTime = toDateFromLocaleStringDate(row.startTime);
        const waitTimeSeconds = Math.max(
          0,
          (startTime.getTime() - deadline.getTime()) / 1000,
        );
        const formattedTime =
          formatter.formatTimespanAs24String(waitTimeSeconds).fullString;

        // Use the new classification model
        const classificationModel = new WaitTimeClassificationModel(
          warningThreshold,
          lateThreshold,
          criticalThreshold,
          customThresholds,
        );
        const classification = classificationModel.classify(waitTimeSeconds);

        return (
          <div
            style={{
              backgroundColor: classification.threshold.backgroundColor,
              color: classification.threshold.color,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            {formattedTime}
          </div>
        );
      },
      classNameTd: (row: JobTimelineDataTableRow) => {
        const deadline = toDateFromLocaleStringDate(row.job.deadline);
        const startTime = toDateFromLocaleStringDate(row.startTime);
        const waitTimeSeconds = Math.max(
          0,
          (startTime.getTime() - deadline.getTime()) / 1000,
        );

        // Use the new classification model
        const classificationModel = new WaitTimeClassificationModel(
          warningThreshold,
          lateThreshold,
          criticalThreshold,
          customThresholds,
        );
        const classification = classificationModel.classify(waitTimeSeconds);

        return classification.threshold.classNameTd;
      },
    },
    {
      field: 'job.deadline',
      title: t`Deadline`,
      isHeader: true,
      hasSort: true,
      hasFilter: true,
      render: (v: string) =>
        format(toDateFromLocaleStringDate(v), 'dd MMM, HH:mm'),
    },
    {
      field: 'startTime',
      title: t`Start Date`,
      isHeader: true,
      hasSort: true,
      hasFilter: true,
      render: (v: string) =>
        format(toDateFromLocaleStringDate(v), 'dd MMM, HH:mm'),
    },
    {
      field: 'completionTime',
      title: t`Completion Time`,
      render: (v: string) =>
        format(toDateFromLocaleStringDate(v), 'dd MMM, HH:mm'),
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'completionTime',
      title: t`Worked Time`,
      render: (v: string, row) => {
        const startTime = toDateFromLocaleStringDate(row.startTime);
        const completionTime = toDateFromLocaleStringDate(v);
        return formatter.formatTimespanAs24String(
          (completionTime.getTime() - startTime.getTime()) / 1000,
        ).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'totalDuration',
      title: t`Total Duration`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'dockingDuration',
      title: t`Docking Time`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'undockingDuration',
      title: t`Undocking Time`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'processingDuration',
      title: t`Loading Time`,
      render: (v: any[], row) => {
        if (!v || !Array.isArray(v)) return <EmptyValueTag />;
        const loadingProcess = v.find(p => p.process === 'LOADING');
        if (!loadingProcess) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(loadingProcess.duration)
          .fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'processingDuration',
      title: t`Handling Time`,
      render: (v: any[], row) => {
        if (!v || !Array.isArray(v)) return <EmptyValueTag />;
        const handlingProcess = v.find(p => p.process === 'HANDLING');
        if (!handlingProcess) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(handlingProcess.duration)
          .fullString;
      },
      hasSort: true,
      hasFilter: true,
    },

    {
      field: 'waitCost',
      title: t`Wait Cost`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatCurrency(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'tardiness',
      title: t`Tardiness`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'jobDetails.jobDistance',
      title: t`Job Distance`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatDistance(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'jobDetails.jobDuration',
      title: t`Job Duration`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatTimespanAs24String(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'jobDetails.jobCost',
      title: t`Job Cost`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatCurrency(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'relatedJobsCost.initial.cost',
      title: t`Initial Cost`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatCurrency(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'relatedJobsCost.scheduled.cost',
      title: t`Scheduled Cost`,
      render: (v: number) => {
        if (!v) return <EmptyValueTag />;
        return formatter.formatCurrency(v).fullString;
      },
      hasSort: true,
      hasFilter: true,
    },
    {
      field: 'jobDetails.details.picklistCount',
      title: t`Tasks`,
      hasSort: true,
      hasFilter: true,
      render: v => formatter.formatInteger(v as any).fullString,
    },
    {
      field: 'jobDetails.details.picklistLineCount',
      title: t`Line Count`,
      hasSort: true,
      hasFilter: true,
      render: v => formatter.formatInteger(v as any).fullString,
    },
    {
      field: 'jobDetails.details.orderCount',
      title: t`Order Count`,
      hasSort: true,
      hasFilter: true,
      render: v => formatter.formatInteger(v as any).fullString,
    },
    {
      field: 'jobDetails.details.netWeight',
      title: t`Weight`,
      hasSort: true,
      hasFilter: true,
      render: v => formatter.formatWeight(v as any).fullString,
    },
    {
      field: 'jobDetails.details',
      title: t`UOM`,
      hasSort: false,
      hasFilter: false,
      render: (v, row) => {
        const uomQty = _.transform(
          (row.jobDetails?.details as unknown as LoadingJobDetails)
            ?.uomQuantities,
          (acc, uom: any) => (acc[uom.uom] = uom.uomQuantity),
          {},
        );
        return (
          <>
            {renderUomsWithTotal(
              uomQty,
              (row.jobDetails.details as unknown as LoadingJobDetails)
                ?.quantity,
              t,
            )}
          </>
        );
      },
    },
    {
      field: 'jobDetails.details.volume',
      title: t`Volume`,
      hasSort: true,
      hasFilter: true,
      render: v => formatter.formatVolume(v as any).fullString,
    },
    {
      field: 'jobDetails.process',
      title: t`Process`,
      hasSort: true,
      hasFilter: true,
      render: (v: any) => <Tag>{v}</Tag>,
    },
  ];

  return config;
}

export default useSimulationJobTimelineTableConfig;
